import React, { useEffect } from "react";

import _ from "lodash";
import Link from "next/link";
import ReactTooltip from "react-tooltip";
import styled, { css } from "styled-components";

import VASPDueDiligenceReviewSummary from "../VASPDueDiligenceReviewSummary";

import CompanyLogo from "~/components/CompanyLogo";
import * as Grid from "~/components/Grid";
import Skeleton from "~/components/Skeleton";
import { Tag, Tags } from "~/components/Tag";
import { Title } from "~/components/Text";
import getCountry from "~/data/getCountry";
import { from } from "~/styles/theme";
import VASPStatus from "~/templates/companies/components/VASPStatus";
import { VASP } from "~/types/vaspid";

const VASPHeaderLogo = styled(CompanyLogo)`
  min-width: 60px;
  height: 60px;
  ${from.tablet(css`
    min-width: 80px;
    height: 80px;
  `)}
`;
const VASPHeaderTitle = styled(Title).attrs({ as: "h1" })`
  &&& {
    margin-bottom: 4px;
    cursor: default;
  }
`;
const VASPHeaderSubTitle = styled.div`
  color: #828282;
  font-size: 18px;
  font-weight: 400;
`;
VASPHeaderTitle.defaultProps = {
  weight: "bolder",
};

function ConditionalWrapper({
  condition,
  wrapper,
  children,
}: {
  condition: boolean;
  wrapper: any;
  children: React.ReactNode;
}) {
  if (condition) {
    return wrapper(children);
  } else {
    return children;
  }
}

type VASPHeaderProps = {
  href?: string;
  vasp?: VASP | null;
  children?: any;
  showIsRegulated?: boolean;
  showJurisdictionLabel?: boolean;
  showDueDiligenceReviewSummary?: boolean;
  onVaspDataUpdated?: () => void;
};

export default function VASPHeader({
  href,
  vasp,
  children,
  showIsRegulated = false,
  showJurisdictionLabel = true,
  showDueDiligenceReviewSummary = false,
  onVaspDataUpdated = () => {
    //default
  },
  ...props
}: VASPHeaderProps & React.ComponentProps<typeof Grid.Columns>) {
  useEffect(() => {
    ReactTooltip.rebuild();
  });

  // TODO: find a better way of making VASPRow clickable
  function LinkWrap(child: any) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return <Link href={href!}>{child}</Link>;
  }

  const jurisdictionName = vasp?.jurisdictions ? getCountry(vasp.jurisdictions) : "";
  const divisionName = vasp?.division ? getCountry(vasp.jurisdictions, vasp?.division) : null;
  const jurisdictionCountry = _.join(_.compact([jurisdictionName, divisionName]), " - ");

  return (
    <Grid.Columns multiline={false} valign="centered" {...props}>
      <Grid.Column narrow>
        {vasp ? (
          <ConditionalWrapper condition={Boolean(href)} wrapper={LinkWrap}>
            <VASPHeaderLogo
              src={vasp.logo || "/assets/vasp-fallback.png"}
              jurisdictions={vasp.jurisdictions}
              className="localize-ignore"
            />
          </ConditionalWrapper>
        ) : (
          <Skeleton width={80} height={80} circle />
        )}
      </Grid.Column>
      <Grid.Column>
        {vasp ? (
          <VASPHeaderTitle style={{ marginBottom: 9 }} title={vasp.did} className="localize-ignore">
            <ConditionalWrapper condition={Boolean(href)} wrapper={LinkWrap}>
              {showIsRegulated ? (
                <span>Is {vasp?.name} regulated?</span>
              ) : (
                <span>
                  <span className="localize-ignore">{vasp?.name}</span>
                  {showJurisdictionLabel && <VASPHeaderSubTitle>{jurisdictionCountry}</VASPHeaderSubTitle>}
                </span>
              )}
            </ConditionalWrapper>
          </VASPHeaderTitle>
        ) : (
          <VASPHeaderTitle>
            <Skeleton count={1} width={200} />
          </VASPHeaderTitle>
        )}
        <Tags>
          {vasp ? (
            <Tag transparent paddingless>
              <VASPStatus
                isVerified={vasp.verificationStatus === "VERIFIED"}
                isClaimed={!!vasp?.hasAdmin}
                isInNetwork={!!vasp.isNotifiable}
              />
            </Tag>
          ) : (
            <Tag transparent>
              <Skeleton count={1} width={100} />
            </Tag>
          )}
        </Tags>
      </Grid.Column>
      {vasp && showDueDiligenceReviewSummary && (
        <VASPDueDiligenceReviewSummary
          reviewedVaspDID={vasp.did}
          vaspReviewStatus={vasp.reviewValue || null}
          vaspReviewIncludeInWidget={vasp.reviewIncludeInWidget}
          vaspReviewNote={vasp.reviewNote || null}
          lastReviewedBy={vasp.reviewedBy}
          lastReviewedDate={vasp.reviewDate ? new Date(vasp.reviewDate) : null}
          onVaspDataUpdated={onVaspDataUpdated}
        />
      )}
      {children && <Grid.Column narrow>{children}</Grid.Column>}
    </Grid.Columns>
  );
}
